import { useNavigate } from 'react-router-dom';
import { Megaphone } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { instituteNoticesApi } from '@/features/institute/notices/api/instituteNotices.api';
import { formatInstituteDate } from '@/features/teacher/utils';
import { useApiQuery } from '@/hooks/useApiQuery';

const NOTICES_SHOWN = 3;

/**
 * What the institute has put up on its own board, newest first.
 *
 * Displays up to 3 notices on the dashboard with a "View all" button when
 * there are more than 3, navigating to the institute notices view in the notices page.
 */
export function InstituteNoticesCard() {
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useApiQuery(() => instituteNoticesApi.list(), []);

  const notices = data?.notices ?? [];
  const preview = notices.slice(0, NOTICES_SHOWN);

  return (
    <div className="card overflow-hidden bg-surface border border-line rounded-[16px] shadow-2xs">
      {/* Header */}
      <div className="border-b border-line bg-surface px-5 py-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-blue-500">
            <Megaphone className="h-4.5 w-4.5" />
          </span>
          <h2 className="text-base font-bold text-ink tracking-tight">Institute Notices</h2>
          {notices.length > 0 && (
            <span className="text-2xs font-bold text-white bg-blue-600 px-2 py-0.5 rounded-full shadow-2xs">
              {notices.length}
            </span>
          )}
        </div>
        {notices.length > NOTICES_SHOWN && (
          <button
            type="button"
            onClick={() => navigate('/teacher/notices?tab=institute')}
            className="text-sm font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors cursor-pointer"
          >
            View all
          </button>
        )}
      </div>

      <div className="flex flex-col gap-3 p-4">
        {isLoading && <SkeletonLines count={3} />}

        {!isLoading && error && (
          <div className="flex flex-col items-start gap-3">
            <Alert tone="critical" title="Could not load the notice board">
              {error.message}
            </Alert>
            <Button variant="secondary" size="sm" onClick={refetch}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !error && notices.length === 0 && (
          <p className="p-4 text-center text-xs text-ink-3 font-medium">
            Nothing from the office yet. Notices your institute puts up appear here.
          </p>
        )}

        {!isLoading &&
          !error &&
          preview.map((notice) => (
            <article
              key={notice.uuid}
              className="flex flex-col gap-2 rounded-[12px] border border-line bg-surface p-2 hover:bg-slate-50/70 dark:hover:bg-surface-2 transition-colors"
            >
              <div className="flex items-start gap-2">
                <span className="mt-1.5 h-2 w-2 rounded-full bg-blue-500 shrink-0" />
                <h3 className="min-w-0 text-[14px] font-bold text-ink leading-snug line-clamp-1 [word-spacing:0.06em]">{notice.title}</h3>
              </div>

              {notice.body && (
                <p className="text-[12px] font-medium text-ink-2 ml-4 line-clamp-2" title={notice.body}>
                  {notice.body}
                </p>
              )}

              <div className="flex items-center justify-between gap-2 ml-4 pt-1">
                {/* Who the notice is addressed to, as the office set it */}
                {notice.audienceLabel && (
                  <span className="px-2 py-0.5 rounded-pill text-2xs font-bold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/15 dark:text-blue-300">
                    {notice.audienceLabel}
                  </span>
                )}
                <span className="ml-auto text-2xs font-medium text-ink-3">
                  {formatInstituteDate(notice.noticeDate)}
                </span>
              </div>
            </article>
          ))}
      </div>
    </div>
  );
}
